import React, { useCallback } from 'react';
import { Avatar, Box, CircularProgress, type SxProps } from '@mui/material';
import { isValidUrl, usernameToColor } from '../../../utils';
import { GlobalVariables } from '../../../pdf/GlobalVariables';
import styles from './UserAvatar.module.scss';

/**
 * Component that shows the user's Auth0 profile image.
 */
export default (props: {
    picture?: string;
    size: number;
    loading?: boolean;
    sxProps?: SxProps;
}) => {
    /** Access relevant state variables from the GlobalVariables store. */
    const first_name = GlobalVariables.authenticatedUser?.firstName ?? '';
    const last_name = GlobalVariables.authenticatedUser?.lastName ?? '';
    const name = GlobalVariables.authenticatedUser?.name;

    /** Deconstruct props. */
    const { size, loading, picture, sxProps } = props;

    /**
     * Provides properties for the User Avatar.
     * @returns Object of properties for the User Avatar.
     */
    const avatarProps = useCallback(
        (fullName: string | undefined) => ({
            bgcolor: usernameToColor(fullName),
            height: size,
            width: size,
            ...sxProps,
            border: '2px solid #FFF',
            borderRadius: '100%',
        }),
        [size],
    );

    /**
     * Defines user's initials.
     * @returns User's initials.
     */
    const getAvatarsInitials = (firstName: string, lastName: string) =>
        firstName?.slice(0, 1).toUpperCase() + lastName?.slice(0, 1).toUpperCase();

    /** Handles loading prosess. */
    if (loading) {
        return (
            <Box sx={{ display: 'flex' }}>
                <CircularProgress />
            </Box>
        );
    }

    /** No user image or the user is different than the logged in user - display initials. */
    if (!picture?.length) {
        return (
            <Avatar className={styles.avatar} sx={{ ...avatarProps(name) }}>
                {getAvatarsInitials(first_name, last_name)}
            </Avatar>
        );
    }

    return (
        <Box
            sx={{
                backgroundImage: `url(${
                    isValidUrl(picture) ? '' : 'data:image/*;base64,'
                }${picture})`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                ...avatarProps(name),
            }}
        />
    );
};
